import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {ICategory, ITag, ITemplate} from "../add-product-form.interface";
import {IDsDropdownOption} from "../../../../../../shared/components/ds-dropdown/ds-dropdown-option";
import {ProductAddHelperService} from "../product-add-helper.service";
import {ActivatedRoute} from "@angular/router";

@Component({
    selector: 'db-product-category',
    templateUrl: './product-category.component.html',
    styleUrls: ['./product-category.component.scss'],
    standalone: false
})
export class ProductCategoryComponent implements OnInit {
  public productId: string;
  @Input() categoryForm: FormGroup;

  @Input('adminUrl') adminUrl: string;

  @Input() set templates(temps: ITemplate[]) {
    const options = temps.map(temp => {
      return {value: temp.id, label: temp.title};
    });
    this.allTemplates = options;
    this.dropdownTemplates = options;
  }

  @Input() set categories(cats: ICategory[]) {
    this.allCategories = cats;
    const {category, subcategory} = this._toDropdownFormat(cats);
    this.dropdownCats = category;
    this.dropdownSubcats = subcategory;
  }

  @Input() set tags(tags: ITag[]) {
    this.allTags = tags.map(tag => ({label: tag.name, value: tag.id}));
    this.dropdownTags = this._filterCollectionDropdown(this.allTags);
  }

  allCategories: ICategory[] = [];
  allTemplates: IDsDropdownOption[] = [];
  allTags: IDsDropdownOption[] = [];
  dropdownCats: IDsDropdownOption[] = [];
  dropdownTemplates: IDsDropdownOption[] = [];
  dropdownTags: IDsDropdownOption[] = [];
  dropdownSubcats: IDsDropdownOption[] = [];
  collectionFormControl = new FormControl('');
  @Output() onCreateCategory: EventEmitter<{ name: string; parent?: number }> = new EventEmitter<{
    name: string;
    parent?: number
  }>();
  @Output() onCreateCollection: EventEmitter<{ name: string; index: number }> = new EventEmitter<{
    name: string;
    index: number
  }>();

  constructor(private _fb: FormBuilder,
              private _addProductHelper: ProductAddHelperService,
              private _route: ActivatedRoute,
  ) {
  }


  modifyRedirectionUrl(tempId: number) {
    return `${this.adminUrl}post.php?post=${tempId}&action=elementor&&twbb_preview_id=${this.productId}`;
  }

  get categoryFormControl(): FormGroup {
    return this.categoryForm.get('category') as FormGroup;
  }

  get subcategoryFormControl(): FormGroup {
    return this.categoryForm.get('subcategory') as FormGroup;
  }

  get collectionFormArray(): FormArray {
    return this.categoryForm?.get('collection') as FormArray;
  }

  get templateFormControl(): FormGroup {
    return this.categoryForm?.get('template') as FormGroup;
  }

  ngOnInit() {
    this.productId = this._route.snapshot.paramMap.get('productId');
  }

  onChooseSubcategory(option: IDsDropdownOption, formGroup: FormGroup) {
    const label = option.label;
    const parent = this.categoryFormControl.get('id').value;
    if (option.value === null) {
      this.onCreateCategory.emit({name: label, parent});
    } else {
      formGroup.patchValue({id: option.value as number});
    }
    this.categoryForm.markAsDirty();
  }

  onChooseCategory(option: IDsDropdownOption, formGroup: FormGroup) {
    const label = option.label;
    if (option.value === null) {
      this.onCreateCategory.emit({name: label});
      this.dropdownSubcats = [];
    } else {
      formGroup.patchValue({id: option.value as number});
      this.dropdownSubcats = this.allCategories
        .filter(cat => cat.parent === option.value)
        .map(cat => ({
          value: cat.id,
          label: cat.name
        }));
    }
    this.subcategoryFormControl.reset();
    this.categoryForm.markAsDirty();
  }

  onChooseCollection(option: IDsDropdownOption) {
    this.collectionFormControl.reset();
    this.collectionFormArray.push(this._addProductHelper.createOptionControl(option.label, option.value));
    if (!option.value) {
      this.onCreateCollection.emit({name: option.label, index: this.collectionFormArray.length - 1});
    } else {
      this.dropdownTags = this._filterCollectionDropdown(this.dropdownTags);
    }
    this.categoryForm.markAsDirty();
  }

  onChooseTemplate(option: IDsDropdownOption, formGroup: FormGroup) {
    formGroup.patchValue({id: option.value as number});
    this.categoryForm.markAsDirty();
  }

  removeTag(index: number) {
    this.collectionFormArray.removeAt(index);
    this.dropdownTags = this._filterCollectionDropdown(this.allTags);
    this.categoryForm.markAsDirty();
  }

  private _toDropdownFormat<T>(options: T & { id: number; name: string; parent?: number }[]): {
    category: IDsDropdownOption[],
    subcategory: IDsDropdownOption[]
  } {
    const categories = {
      category: [],
      subcategory: []
    };
    options.forEach(option => {
      if (option.parent) {
        categories.subcategory.push({
          value: option.id,
          label: option.name
        });
      } else {
        categories.category.push({
          value: option.id,
          label: option.name
        });
      }
    });

    return categories;
  }

  optionInputChange(value: string, formControl: FormGroup) {
    if (!value) {
      formControl.reset();
      formControl.markAsDirty();
    }
  }

  private _filterCollectionDropdown(dropdown: IDsDropdownOption[]): IDsDropdownOption[] {
    if (!this.collectionFormArray) {
      return [];
    }
    const controls = this.collectionFormArray.controls;
    return dropdown.filter(tag => !controls.some(control => control.get('id') && control.get('id').value === tag.value));
  }
}
